/*
  cq-hardness-lookup.js: one hardness answer for a ZIP code, shared by the water hardness map and written so the header
  ZIP check and the softener quiz can use it too (then every tool gives the same answer for the same ZIP).

  window.CQHardnessLookup.configure({data, coords, wells, areaBase, areaMissing})
  window.CQHardnessLookup.lookup(zip, {source: 'city' | 'well'}) resolves to a result (shape at the bottom).
  window.CQHardnessLookup.data() resolves to the map dataset (cities with lat, lng, ppm, gpg, zips).

  City water, best evidence first (AJ 2026-10-19: "i typed in a zip and it gave me a whole different county"):
    exact    the map file's ZIP row when it is a sourced utility figure AND the ZIP's own Census point lies within
             15 miles of that city's point (the map file files some ZIPs under a city 20 to 700 miles away)
    utility  the Water Wizard ZIP database: the first utility listed for the ZIP with a tier 1 (cited finished water)
             or tier 2 (the utility's latest EPA record) hardness figure. A Wizard "h" match that is the ZIP's first
             utility is its own utility; any other "h" or an "m" match reads "likely served by" (likely: true).
             "l" matches are never used.
    exact    an older unverified or source-water map row, with the same 15 mile guard (weak: true)
    near     the nearest sourced city within 50 miles of the ZIP's Census point; it is never the shopper's place
    miss     no figure
  Private well (never a city figure as a well answer):
    well       typical hardness of measured wells near the ZIP, 2021 on
    well-miss  no recent well results near the ZIP

  A city whose own point lies more than 30 miles from every one of its ZIP points (Honolulu's sits about 700 miles off)
  is dropped from the nearest-city search and from the maps. Every file loads only when a step needs it, and a file
  that fails to load skips its step instead of failing the lookup. The data file declares global consts, so it is
  never injected twice (another script may have loaded it).
*/
(function () {
  'use strict';
  if (window.CQHardnessLookup) return;

  var cfg = {data: '', coords: '', wells: '', areaBase: 'https://help.crystolquest.shop/wizard-data/', areaMissing: '', useArea: true, nearMiles: 50, regionalMiles: 250, guardMiles: 15, badPointMiles: 30, timeout: 8000};
  var loads = {};
  var areas = {};
  var GPG = 17.1;
  var BANDS = [
    {key: 'soft', label: 'Soft', max: 60},
    {key: 'moderate', label: 'Moderately hard', max: 120},
    {key: 'hard', label: 'Hard', max: 180},
    {key: 'very-hard', label: 'Very hard', max: Infinity}
  ];

  function readGlobal(name) {
    /* eslint-disable no-undef */
    try {
      if (name === 'data') return typeof waterHardnessData !== 'undefined' ? waterHardnessData : null;
      if (name === 'coords') return typeof waterHardnessZipCoords !== 'undefined' ? waterHardnessZipCoords : null;
      if (name === 'wells') return typeof waterHardnessGroundwater !== 'undefined' ? waterHardnessGroundwater : null;
    } catch (_) { return null; }
    return null;
  }

  function loadScript(name) {
    var have = readGlobal(name);
    if (have) return Promise.resolve(have);
    var url = cfg[name];
    if (!url) return Promise.reject(new Error('no url for ' + name));
    if (loads[name]) return loads[name];
    loads[name] = new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(new Error('timeout ' + name)); }, 20000);
      var done = function () { clearTimeout(timer); var v = readGlobal(name); if (v) resolve(v); else reject(new Error('empty ' + name)); };
      var fail = function () { clearTimeout(timer); reject(new Error('failed ' + name)); };
      var script = null;
      var tags = document.getElementsByTagName('script');
      for (var i = 0; i < tags.length; i++) if (tags[i].getAttribute('src') === url) { script = tags[i]; break; }
      if (script) {
        script.addEventListener('load', done, {once: true});
        script.addEventListener('error', fail, {once: true});
        setTimeout(function () { if (readGlobal(name)) done(); }, 0);
        return;
      }
      script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.addEventListener('load', done, {once: true});
      script.addEventListener('error', function () { script.remove(); fail(); }, {once: true});
      document.head.appendChild(script);
    }).catch(function (e) { delete loads[name]; throw e; });
    return loads[name];
  }

  function loadArea(z3) {
    if (!cfg.useArea || !/^\d{3}$/.test(z3)) return Promise.resolve(null);
    if ((' ' + cfg.areaMissing + ' ').indexOf(' ' + z3 + ' ') >= 0) return Promise.resolve(null);
    if (areas[z3]) return areas[z3];
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, cfg.timeout);
    areas[z3] = fetch(cfg.areaBase + 'cq-wiz-area-' + z3 + '.json', {credentials: 'omit', mode: 'cors', signal: ctl ? ctl.signal : undefined})
      .then(function (r) { if (r.status === 404) return null; if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (d) { clearTimeout(timer); return d; }, function () { clearTimeout(timer); delete areas[z3]; return null; });
    return areas[z3];
  }

  // Abbreviations a shopper would not recognise, spelled out (the 2026-10-18 review: no "MDWASA" on a result).
  var SPELLED = {
    MDWASA: 'Miami-Dade Water and Sewer Department', SAWS: 'San Antonio Water System', MWRA: 'Massachusetts Water Resources Authority',
    OUC: 'Orlando Utilities Commission', KUB: 'Knoxville Utilities Board', LCRA: 'Lower Colorado River Authority', WASA: 'Water and Sewer Authority',
    WMATUA: 'Western Maryland Area Trust Utility Authority', MUD: 'Municipal Utility District', PUD: 'Public Utility District',
    SUD: 'Special Utility District', WCID: 'Water Control and Improvement District', PWSD: 'Public Water Supply District',
    WSC: 'Water Supply Corporation', HNL: 'Honolulu', DPW: 'Department of Public Works', BWS: 'Board of Water Supply'
  };
  var ACRONYM = /^(BIA|USA|US|AFB|NAS|USMC|HOA|ISD|WSSC|JEA|EPCOR|DC|NYC|NJ|II|III|IV|LLC|RV|UV)$/;
  var KEEP_WORD = /^(ST|MT|MTN|HWY|CNTY|PK|FT|CTR|RD|JT|SYS|WTR|BY|SKY|LYNN|TWP|PCT|MC|MS|DR|LN|BLVD|PL|CT|SW|NW|NE|SE)$/;
  var INVERTED = /^(.*?)[,\s]+(CITY|TOWN|VILLAGE|VIL|BOROUGH|BORO|TOWNSHIP|TWP|COUNTY)\s+OF$/i;
  var INVERTED_WORD = {VIL: 'Village', BORO: 'Borough', TWP: 'Township'};
  // Same rules as the Water Wizard's utility names (cq-wiz-adapter.js titleCase), plus spaced hyphens read as commas
  // and the abbreviations above spelled out.
  function utilityName(raw) {
    var t = String(raw || '').replace(/\s*[\u2013\u2014]\s*|\s+-+\s+/g, ', ').replace(/\s+/g, ' ').trim();
    t = t.replace(/^(.+?)-(CITY|TOWN|VILLAGE|COUNTY) OF$/i, '$1 $2 OF');
    var mixed = t !== t.toUpperCase();
    if (mixed) t = t.replace(/^(.+?),?\s+(City|Town|Village|County|Borough|Township) of$/i, function (m, a, b) { return b.charAt(0).toUpperCase() + b.slice(1).toLowerCase() + ' of ' + a; });
    if (!mixed) {
      var inv = t.match(INVERTED);
      if (inv && inv[1]) t = (INVERTED_WORD[inv[2].toUpperCase()] || inv[2]) + ' of ' + inv[1];
    }
    var out = t.split(/(\s+|[-\/(),#]+)/).map(function (tok) {
      if (!/[A-Za-z]/.test(tok)) return tok;
      var up = tok.toUpperCase();
      if (SPELLED[up] && tok === up) return SPELLED[up];
      if (mixed) return tok;
      if (ACRONYM.test(up) || (/^[A-Z]{2,6}$/.test(up) && !/[AEIOU]/.test(up) && !KEEP_WORD.test(up) && tok === up)) return up;
      return tok.toLowerCase().replace(/^([a-z])/, function (m) { return m.toUpperCase(); });
    }).join('');
    if (mixed) return out;
    return out.replace(/\b(Of|And|The|For|Co)\b/g, function (w) { return w === 'Co' ? 'Co.' : w.toLowerCase(); }).replace(/^./, function (m) { return m.toUpperCase(); });
  }

  function bandIndex(ppm) {
    var p = Math.round(Number(ppm));
    for (var i = 0; i < BANDS.length; i++) if (p <= BANDS[i].max) return i;
    return BANDS.length - 1;
  }
  function figures(ppm, gpg) {
    var p = Math.round(Number(ppm));
    var g = gpg != null && isFinite(gpg) ? Math.round(Number(gpg) * 10) / 10 : Math.round(p / GPG * 10) / 10;
    var b = bandIndex(p);
    return {ppm: p, gpg: g, band: b, bandKey: BANDS[b].key, bandLabel: BANDS[b].label};
  }
  function assign(target, src) { for (var k in src) if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k]; return target; }

  function miles(a, b, c, d) {
    var r = 3958.8, rad = Math.PI / 180, dLat = (c - a) * rad, dLng = (d - b) * rad;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(a * rad) * Math.cos(c * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * r * Math.asin(Math.sqrt(h));
  }
  // Existing water-data pipeline plausibility ceiling; unusable evidence falls through, never gets capped.
  function validPpm(value) { return typeof value === 'number' && isFinite(value) && value >= 0 && value <= 5000; }
  function sourced(c) { return c && validPpm(c.ppm) && (c.basis === 'finished' || c.basis === 'mixed'); }

  function point(coords, zip) {
    try { var p = coords && coords.get(zip); return p ? {lat: p.lat, lng: p.lng, approx: !!p.approx} : null; } catch (_) { return null; }
  }

  // Cities whose own point is far from every ZIP filed under them, and the sourced cities by utility ID.
  var checked = null;
  function cityIndex(data, coords) {
    if (checked && checked.data === data && checked.coords === coords) return checked;
    var bad = {}, byPws = {};
    (data.cities || []).forEach(function (c, i) {
      if (sourced(c) && c.pwsid && !byPws[c.pwsid]) byPws[c.pwsid] = c;
      if (!coords) return;
      var near = false, any = false;
      for (var j = 0; j < (c.zips || []).length && !near; j++) {
        var p = point(coords, c.zips[j]);
        if (!p || p.approx) continue;
        any = true;
        if (miles(p.lat, p.lng, c.lat, c.lng) <= cfg.badPointMiles) near = true;
      }
      if (any && !near) bad[i] = true;
    });
    checked = {data: data, coords: coords, bad: bad, byPws: byPws, hasCoords: !!coords};
    return checked;
  }
  function goodCities(data, coords) {
    var ix = cityIndex(data, coords);
    return (data.cities || []).filter(function (c, i) { return !ix.bad[i] && validPpm(c.ppm); });
  }

  function cityResult(kind, zip, c, extra) {
    var weak = !sourced(c);
    return assign(assign({kind: kind, zip: zip, source: 'city', weak: kind === 'exact' && weak, basis: c.basis || null,
      place: {city: c.city, state: c.state, lat: c.lat, lng: c.lng}, utility: c.utility ? utilityName(c.utility) : null, year: c.year || null,
      range: Array.isArray(c.range) ? c.range : null}, figures(c.ppm, c.gpg)), extra || {});
  }

  // The first Wizard utility for the ZIP with a usable hardness figure (tier 1 or 2), and how sure the match is.
  function wizardUtility(area, zip) {
    var list = area && area.z && area.z[zip];
    if (!list || !list.length) return null;
    for (var i = 0; i < list.length; i++) {
      var m = list[i], u = area.s && area.s[m[0]];
      if (m[1] !== 'h' && m[1] !== 'm') continue;
      if (u && Array.isArray(u.h) && typeof u.h[0] === 'number' && validPpm(u.h[0] * GPG) && u.h[2] >= 1 && u.h[2] <= 2) return {id: m[0], conf: m[1], first: i === 0, u: u};
    }
    return null;
  }

  function nearest(data, coords, pt, cap) {
    var best = null, bestMiles = Infinity;
    cap = cap || cfg.nearMiles;
    goodCities(data, coords).forEach(function (c) {
      if (!sourced(c) || Math.abs(c.lat - pt.lat) > cap / 69 + 0.1) return;
      var m = miles(pt.lat, pt.lng, c.lat, c.lng);
      if (m < bestMiles) { bestMiles = m; best = c; }
    });
    return best && bestMiles <= cap ? {city: best, miles: Math.round(bestMiles * 10) / 10} : null;
  }

  function cityLookup(zip) {
    return loadScript('data').then(function (data) {
      return loadScript('coords').then(null, function () { return null; }).then(function (coords) {
        var pt = point(coords, zip);
        var ix = cityIndex(data, coords);
        var exact = typeof data.findCityByZip === 'function' ? data.findCityByZip(zip) : null;
        var dist = exact && pt && !pt.approx ? miles(pt.lat, pt.lng, exact.lat, exact.lng) : null;
        // a ZIP filed under a city far from its own Census point is not that city's ZIP
        var placed = exact && validPpm(exact.ppm) && dist != null && dist <= cfg.guardMiles;
        var guarded = exact && dist != null && !placed ? {city: exact.city, state: exact.state, miles: Math.round(dist)} : null;
        if (placed && sourced(exact)) return cityResult('exact', zip, exact, {point: pt, cityMiles: dist == null ? null : Math.round(dist * 10) / 10});
        return loadArea(zip.slice(0, 3)).then(function (area) {
          var w = wizardUtility(area, zip);
          if (w) {
            var mapCity = w.u && ix.byPws[w.id];
            var g = Number(w.u.h[0]);
            var fig = mapCity ? figures(mapCity.ppm, mapCity.gpg) : figures(g * GPG, g);
            return assign({kind: 'utility', zip: zip, source: 'city', weak: false, tier: w.u.h[2], matchConf: w.conf,
              likely: !(w.conf === 'h' && w.first), utility: utilityName((mapCity && mapCity.utility) || w.u.n), year: (mapCity && mapCity.year) || w.u.h[1] || null,
              place: null, point: pt, guarded: guarded}, fig);
          }
          if (placed) return cityResult('exact', zip, exact, {point: pt});
          if (!pt) return {kind: 'miss', zip: zip, source: 'city', point: null, guarded: guarded};
          var hit = pt.approx ? null : nearest(data, coords, pt);
          if (hit) return cityResult('near', zip, hit.city, {miles: hit.miles, point: pt, guarded: guarded});
          // No report within nearMiles, or only the ZIP area's average point (PO box ZIPs): show the closest report
          // as a regional estimate rather than a dead end (AJ 2026-10-20). Past regionalMiles it is too far to stand in.
          var far = nearest(data, coords, pt, cfg.regionalMiles);
          if (far) return cityResult('near', zip, far.city, {miles: far.miles, point: pt, guarded: guarded, regional: true, areaPoint: !!pt.approx});
          return {kind: 'miss', zip: zip, source: 'city', point: pt.approx ? null : pt, guarded: guarded};
        });
      });
    });
  }

  var AREA_WORDS = {z: 'in your ZIP code', '10': 'within 10 miles', '25': 'within 25 miles', c: 'in your county'};
  function wellLookup(zip) {
    return loadScript('wells').then(function (wells) {
      var r = wells[zip];
      var withPoint = function (res) { return loadScript('coords').then(function (c) { res.point = point(c, zip); return res; }, function () { res.point = null; return res; }); };
      if (!r || !validPpm(r[0])) return withPoint({kind: 'well-miss', zip: zip, source: 'well'});
      return withPoint(assign({kind: 'well', zip: zip, source: 'well', weak: false,
        well: {low: r[1], high: r[2], wells: r[3], area: r[4], areaWords: AREA_WORDS[r[4]] || 'nearby', medianYear: r[5], latestYear: r[6], conf: r[7], share: r[8]}},
      figures(r[0])));
    });
  }

  function lookup(zip, opts) {
    var z = String(zip || '').trim();
    if (!/^\d{5}$/.test(z)) return Promise.resolve({kind: 'invalid', zip: z});
    var source = opts && opts.source === 'well' ? 'well' : 'city';
    var run = source === 'well' ? wellLookup(z) : cityLookup(z);
    return run.catch(function (e) { return {kind: 'error', zip: z, source: source, error: String(e && e.message || e)}; });
  }

  window.CQHardnessLookup = {
    configure: function (o) { assign(cfg, o || {}); checked = null; return this; },
    lookup: lookup,
    data: function () { return loadScript('data'); },
    coords: function () { return loadScript('coords'); },
    point: function (zip) { return loadScript('coords').then(function (c) { return point(c, zip); }, function () { return null; }); },
    // sourced cities with a trustworthy point, for drawing (unverified rows and misplaced points are left off maps)
    mapCities: function () {
      return loadScript('data').then(function (data) {
        return loadScript('coords').then(null, function () { return null; }).then(function (coords) {
          return goodCities(data, coords).filter(sourced);
        });
      });
    },
    figures: figures,
    bandIndex: bandIndex,
    utilityName: utilityName,
    miles: miles,
    BANDS: BANDS
  };
  /* result: {kind, zip, source, ppm, gpg, band, bandKey, bandLabel, weak, likely, basis, place {city, state, lat, lng},
     utility, year, range, tier, matchConf, miles, cityMiles, guarded {city, state, miles}, point {lat, lng, approx},
     well {low, high, wells, area, areaWords, medianYear, latestYear, conf, share}, error} */
})();
